import { supabase } from '../supabase';
import { useQueryClient } from '@tanstack/react-query';
import { useEffect, useRef, useCallback } from 'react';
import { UserNotification, Notification as NotificationType } from '@shiftly/shared/src/schema';
import { DateTime } from 'luxon';
import { useAuth } from '../../../contexts/AuthContext';

const convertDatabaseRecordToUserNotification = (record: any): UserNotification & { notification: NotificationType } => {
  return {
    id: record.id,
    notification_id: record.notification_id,
    user_id: record.user_id,
    read: record.read,
    read_at: record.read_at ? DateTime.fromISO(record.read_at, { zone: 'UTC' }) : undefined,
    created_at: DateTime.fromISO(record.created_at, { zone: 'UTC' }),
    notification: {
      id: record.notifications.id,
      title: record.notifications.title,
      message: record.notifications.message,
      type: record.notifications.type,
      group: record.notifications.group,
      created_at: DateTime.fromISO(record.notifications.created_at, { zone: 'UTC' }),
      created_by: record.notifications.created_by,
      target_users: record.notifications.target_users || undefined,
      link: record.notifications.link || undefined,
      source: record.notifications.source || undefined,
      source_user_id: record.notifications.source_user_id || undefined,
    }
  };
};

export interface RealtimeNotificationManagerOptions {
  enabled?: boolean;
  onError?: (error: any) => void;
}

export const useRealtimeNotificationManager = (options: RealtimeNotificationManagerOptions = {}) => {
  const { enabled = true, onError } = options;
  const { user, session, loading } = useAuth();
  const queryClient = useQueryClient();
  const subscriptionRef = useRef<any>(null);

  // Only enable if auth is ready and user is authenticated
  const shouldEnable = enabled && !loading && !!user && !!session;

  const addNewNotificationToQueries = useCallback((newRecord: any) => {
    const notificationQueries = queryClient.getQueriesData<(UserNotification & { notification: NotificationType })[]>({ 
      queryKey: ['notifications'] 
    });

    if (process.env.NODE_ENV !== 'production') {
      console.log('New notification received:', { recordId: newRecord?.id });
    }

    notificationQueries.forEach(([queryKey, oldData]) => {
      if (!Array.isArray(oldData)) return;

      queryClient.setQueryData(queryKey, (currentData: (UserNotification & { notification: NotificationType })[] | undefined) => {
        if (!currentData || !newRecord.notifications) return currentData;

        const newUserNotification = convertDatabaseRecordToUserNotification(newRecord);
        
        if (shouldIncludeNotificationInQuery(newUserNotification, queryKey)) {
          const exists = currentData.some(notif => notif.id === newUserNotification.id);
          if (!exists) {
            return [...currentData, newUserNotification].sort((a, b) =>
              b.created_at.toMillis() - a.created_at.toMillis()
            );
          }
        }

        return currentData;
      });
    });

    // Refresh unread count for new notifications
    queryClient.refetchQueries({ queryKey: ['notifications', undefined, 'unread'] });
  }, [queryClient]);

  const shouldIncludeNotificationInQuery = (userNotification: UserNotification & { notification: NotificationType }, queryKey: readonly unknown[]): boolean => {
    const user_id = queryKey[1] as string;
    return !!user_id && userNotification.user_id === user_id;
  };

  useEffect(() => {
    const { data: listener } = supabase.auth.onAuthStateChange((_event, session) => {
      if (session) {
        if (subscriptionRef.current) subscriptionRef.current.unsubscribe();
        subscriptionRef.current = supabase
          .channel('realtime notification subscription', { config: { private: true } })
          .on(
            'postgres_changes',
            {
              event: 'INSERT',
              schema: 'public',
              table: 'user_notifications',
            },
            async (payload) => {
              if (payload.new) {
                try {
                  const { data: notificationData } = await supabase
                    .from('user_notifications')
                    .select(`
                      *,
                      notifications (*)
                    `)
                    .eq('id', payload.new.id)
                    .single();

                  if (notificationData) {
                    addNewNotificationToQueries(notificationData);
                  }
                } catch (error) {
                  console.error('Error fetching notification details:', error);
                }
              }
            }
          )
          .subscribe((status, err) => {
            console.log('Realtime notification subscription status:', status);
            if (err && onError) {
              onError(err);
            }
          });
      } else {
        console.log('User signed out, cleaning up realtime notification subscription');
        if (subscriptionRef.current) {
          subscriptionRef.current.unsubscribe();
          subscriptionRef.current = null;
        }
      }
    });

    return () => {
      console.log('Cleaning up realtime notification subscription');
      listener?.subscription.unsubscribe();
      if (subscriptionRef.current) {
        subscriptionRef.current.unsubscribe();
        subscriptionRef.current = null;
      }
    };
  }, [shouldEnable, addNewNotificationToQueries, onError]);

  return {
    isConnected: !!subscriptionRef.current
  };
};